import React, { useEffect, useMemo, useState } from "react";
import { Button, Loading, MessageBanner, Modal, ModalFooter, RadioButton, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchProductNumbersByProductId } from "../../api/productNumberService";
import { barcodeFilename, barcodeTextForNumber, detectSymbology, } from "../../utils/barcodeSymbology";
import { generateBarcodePngDataUrl, generateBarcodeSvg, } from "../../utils/barcodeGenerate";
import { svgToEps, svgToPdf } from "../../utils/barcodeTranscode";
import css from "./BarcodeGeneratorModal.module.css";
/** Triggers a browser download of the given blob under a file name. */
var downloadBlob = function (blob, filename) {
    var url = URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};
/**
 * Modal that generates a scannable barcode (ISBN/ISMN) for one of a product's
 * assigned product numbers via bwip-js and lets the user download it as SVG, EPS,
 * PDF or PNG.
 *
 * The product numbers are loaded from the same query key as the product detail
 * page, so the two share the react-query cache. When the product has no assigned
 * number the modal explains this and generates nothing.
 */
export var BarcodeGeneratorModal = function (_a) {
    var open = _a.open, productId = _a.productId, onClose = _a.onClose;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var _b = useQuery(["productNumbers", productId], function () { return fetchProductNumbersByProductId(ky, productId); }, { enabled: open && !!productId }), data = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    var numbers = useMemo(function () { return ((data === null || data === void 0 ? void 0 : data.items) || []).filter(function (n) { return barcodeTextForNumber(n); }); }, [data]);
    var _c = useState(undefined), selectedId = _c[0], setSelectedId = _c[1];
    // Preselect the marked/primary number (or the first one) once numbers load.
    useEffect(function () {
        if (!numbers.length) {
            setSelectedId(undefined);
            return;
        }
        var stillValid = numbers.some(function (n) { return n.id === selectedId; });
        if (stillValid)
            return;
        var preferred = numbers.find(function (n) { return n.selected; }) || numbers[0];
        setSelectedId(preferred.id);
    }, [numbers, selectedId]);
    var selected = useMemo(function () { return numbers.find(function (n) { return n.id === selectedId; }); }, [numbers, selectedId]);
    // Generate the SVG (single geometry source) for the selected number.
    var generated = useMemo(function () {
        if (!selected)
            return null;
        var text = barcodeTextForNumber(selected);
        try {
            var svg = generateBarcodeSvg(text, detectSymbology(text));
            return { svg: svg, error: false };
        }
        catch (_a) {
            return { svg: null, error: true };
        }
    }, [selected]);
    var previewSrc = (generated === null || generated === void 0 ? void 0 : generated.svg)
        ? "data:image/svg+xml;charset=utf-8,".concat(encodeURIComponent(generated.svg))
        : null;
    var canDownload = !!selected && !!(generated === null || generated === void 0 ? void 0 : generated.svg);
    var handleDownloadSvg = function () {
        if (!selected || !(generated === null || generated === void 0 ? void 0 : generated.svg))
            return;
        downloadBlob(new Blob([generated.svg], { type: "image/svg+xml" }), barcodeFilename(selected, "svg"));
    };
    var handleDownloadEps = function () {
        if (!selected || !(generated === null || generated === void 0 ? void 0 : generated.svg))
            return;
        downloadBlob(new Blob([svgToEps(generated.svg)], { type: "application/postscript" }), barcodeFilename(selected, "eps"));
    };
    var handleDownloadPdf = function () {
        if (!selected || !(generated === null || generated === void 0 ? void 0 : generated.svg))
            return;
        downloadBlob(new Blob([svgToPdf(generated.svg)], { type: "application/pdf" }), barcodeFilename(selected, "pdf"));
    };
    var handleDownloadPng = function () {
        if (!selected)
            return;
        var text = barcodeTextForNumber(selected);
        var dataUrl = generateBarcodePngDataUrl(text, detectSymbology(text));
        var link = document.createElement("a");
        link.href = dataUrl;
        link.download = barcodeFilename(selected, "png");
        link.click();
    };
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleDownloadSvg, disabled: !canDownload },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.download.svg" })),
        React.createElement(Button, { buttonStyle: "default", onClick: handleDownloadEps, disabled: !canDownload },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.download.eps" })),
        React.createElement(Button, { buttonStyle: "default", onClick: handleDownloadPdf, disabled: !canDownload },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.download.pdf" })),
        React.createElement(Button, { buttonStyle: "default", onClick: handleDownloadPng, disabled: !canDownload },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.download.png" })),
        React.createElement(Button, { buttonStyle: "default", onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.close" }))));
    return (React.createElement(Modal, { open: open, onClose: onClose, label: formatMessage({
            id: "ui-br-agency.product.barcode.modal.heading",
        }), footer: footer, size: "medium", dismissible: true },
        isLoading && React.createElement(Loading, null),
        isError && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.error" }))),
        !isLoading && !isError && numbers.length === 0 && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.empty" }))),
        !isLoading && !isError && numbers.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("fieldset", { className: css.numberFieldset },
                React.createElement("legend", { className: css.legend },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.select.legend" })),
                numbers.map(function (number) { return (React.createElement(RadioButton, { key: number.id, name: "barcodeProductNumber", label: number.fullFormattedValue || barcodeTextForNumber(number), checked: number.id === selectedId, onChange: function () { return setSelectedId(number.id); } })); })),
            React.createElement("div", { className: css.preview }, previewSrc ? (React.createElement("img", { className: css.previewImage, src: previewSrc, alt: formatMessage({
                    id: "ui-br-agency.product.barcode.preview.alt",
                }) })) : (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-br-agency.product.barcode.error" }))))))));
};
